import {
  trackEvent,
  useAnalytics,
  useReadCloudAuth,
  useHasWindowFocus,
} from '@readcloud/common';

// Check if running in Tauri environment
const isTauri = typeof window !== 'undefined' && '__TAURI__' in window;
import { LoadingIcon } from '@readcloud/component-library';
import { FeedbackUI } from '@readcloud/feedback-ui/FeedbackUI';
import {
  annotationsActions,
  applicationActions,
  applicationSelectors,
  authActions,
  authSelectors,
  bookmarksActions,
  booksActions,
  cloudsActions,
  commentsActions,
  tagsActions,
  useAppDispatch,
  usersActions,
} from '@readcloud/state';
import React from 'react';
import { useSelector } from 'react-redux';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import { ReaderPage } from './ReaderPage';
import { ContentPage } from './ContentPage';
import { CloudsAndContentPage } from './CloudsAndContentPage';
import { Page } from './Page';
import { NativeUrlHandler } from './NativeUrlHandler';
import { getSubdomainAssets } from '@readcloud/common/lib/utils/utils';

export const App: React.FC = (props) => {
  const analytics = useAnalytics();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { user } = useReadCloudAuth();

  const deltaIntervalHandle = React.useRef(undefined);

  const [isBootstrapped, setIsBootstrapped] = React.useState<boolean>(false);
  const access_token = useSelector(authSelectors.getAccessToken);
  const config = useSelector(applicationSelectors.getAppConfig);
  const appState = useSelector(applicationSelectors.getApplcationState);
  //get all the necessary data and store it in redux for Rreader

  const [refreshing, setRefreshing] = React.useState(false);
  const refresh = async (withoutRefreshingState = false) => {
    if (!withoutRefreshingState) setRefreshing(true);
    try {
      await Promise.all([
        dispatch(usersActions.asyncActions.getConnectedUsers({})),
        dispatch(cloudsActions.asyncActions.getClouds({})),
        dispatch(booksActions.asyncActions.getConnectedBooks({})),
        // dispatch(applicationActions.asyncActions.getAppConfig({})),
      ]);
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      if (!withoutRefreshingState) setRefreshing(false);
    }
  };

  const windowIsFocused = useHasWindowFocus((focussed) => {
    if (focussed) {
      refresh(true);
    }
  });

  //get delta for everything.
  React.useEffect(() => {
    let cancelled = false;
    if (
      windowIsFocused &&
      config?.deltaPollingIntervalSec &&
      appState.lastPoll.allServerMillisec
    ) {
      clearInterval(deltaIntervalHandle.current);
      deltaIntervalHandle.current = setInterval(() => {
        if (cancelled) return;
        dispatch(applicationActions.asyncActions.getDeltaAll({}));
      }, config?.deltaPollingIntervalSec * 1000 || 300000);
    }
    return () => {
      cancelled = true;
      clearInterval(deltaIntervalHandle.current);
    };
  }, [
    config?.deltaPollingIntervalSec,
    appState.lastPoll.allServerMillisec,
    windowIsFocused,
  ]);

  React.useEffect(() => {
    if (access_token) {
      // Clear stale state from a previous user before loading new data.
      // This prevents old content from flashing while the new get/all
      // requests are in flight.
      dispatch(booksActions.clearBooks());
      dispatch(cloudsActions.clearClouds());
      dispatch(annotationsActions.clearAnnotations());
      dispatch(bookmarksActions.clearBookmarks());
      dispatch(usersActions.clearUsers());
      dispatch(commentsActions.clearComments());
      dispatch(tagsActions.clearTags());

      refresh();
      setIsBootstrapped(true);
    }
  }, [access_token]);

  // Store pending deep link for when app is cold-started
  const pendingDeepLinkRef = React.useRef<string | null>(null);

  // Function to navigate from a deep link URL
  const navigateFromDeepLink = React.useCallback(
    (deepLinkUrl: string) => {
      try {
        // Parse Universal Link URL: https://app.readcloud.com/native/read/{cloudId}/{bookId}?p={location}
        // Pathname is /native/read/{cloudId}/{bookId}. winc.app.readcloud.com uses the same path shape.
        const url = new URL(deepLinkUrl);

        console.log('URL parsed:', {
          pathname: url.pathname,
          search: url.search,
        });

        // Extract cloudId and bookId from pathname: /native/read/{cloudId}/{bookId}
        const pathParts = url.pathname.split('/').filter(Boolean);
        let cloudId: string | null = null;
        let bookId: string | null = null;

        const readIdx = pathParts.indexOf('read');
        if (readIdx !== -1 && pathParts.length >= readIdx + 3) {
          cloudId = decodeURIComponent(pathParts[readIdx + 1]);
          bookId = decodeURIComponent(pathParts[readIdx + 2]);
        }

        const location = url.searchParams.get('p');

        console.log('Extracted values:', { cloudId, bookId, location });

        if (bookId && cloudId) {
          // Navigate to the reader with the book
          const readerPath = location
            ? `/read/${cloudId}/${bookId}?p=${encodeURIComponent(location)}`
            : `/read/${cloudId}/${bookId}`;
          console.log('Navigating to:', readerPath);
          history.push(readerPath);
        } else {
          console.warn(
            'Invalid deep link: missing bookId or cloudId. Path:',
            url.pathname
          );
        }
      } catch (error) {
        console.error('Error parsing deep link:', error);
      }
    },
    [history]
  );

  // Listen for reader deep link events (Tauri native app)
  React.useEffect(() => {
    if (!isTauri) return;

    let unlisten: (() => void) | undefined;

    const setupListener = async () => {
      const { listen } = await import('@tauri-apps/api/event');
      unlisten = await listen<string>('reader-deep-link', (event) => {
        const deepLinkUrl = event.payload;
        console.log('Reader deep link received:', deepLinkUrl);
        console.log('App bootstrapped:', isBootstrapped);

        if (isBootstrapped) {
          // App is ready, navigate immediately
          navigateFromDeepLink(deepLinkUrl);
        } else {
          // App not ready yet, queue for later
          console.log('App not bootstrapped yet, queuing deep link');
          pendingDeepLinkRef.current = deepLinkUrl;
        }
      });
    };

    setupListener();

    return () => {
      if (unlisten) {
        unlisten();
      }
    };
  }, [isBootstrapped, navigateFromDeepLink]);

  // Process pending deep link once app is bootstrapped
  React.useEffect(() => {
    if (isBootstrapped && pendingDeepLinkRef.current) {
      console.log(
        'App bootstrapped, processing pending deep link from ref:',
        pendingDeepLinkRef.current
      );
      navigateFromDeepLink(pendingDeepLinkRef.current);
      pendingDeepLinkRef.current = null;
    }
  }, [isBootstrapped, navigateFromDeepLink]);

  // Check for pending deep link from Rust side (for cold start scenarios)
  // Wait for isBootstrapped AND initial data load (!refreshing) to complete
  React.useEffect(() => {
    console.log('Deep link effect check:', {
      isTauri,
      isBootstrapped,
      refreshing,
    });

    // Wait until app is bootstrapped AND initial data load is complete
    if (!isTauri || !isBootstrapped || refreshing) return;

    const checkPendingDeepLink = async () => {
      try {
        console.log('Checking for pending deep link from Rust...');
        const { invoke } = await import('@tauri-apps/api/core');
        const pendingUrl = await invoke<string | null>('get_pending_deep_link');
        console.log('Pending deep link result:', pendingUrl);
        if (pendingUrl) {
          console.log('Found pending deep link, navigating...');
          navigateFromDeepLink(pendingUrl);
        }
      } catch (error) {
        console.error('Error checking pending deep link:', error);
      }
    };

    checkPendingDeepLink();
  }, [isBootstrapped, refreshing, navigateFromDeepLink]);

  //##############################################
  //WEBAPP USER AUTH

  React.useEffect(() => {
    if (user) {
      dispatch(
        authActions.asyncActions.updateAuth({
          user: {
            access_token: user.accessToken,
            email: user.email,
            ...user,
          },
        })
      ).then(() => {
        //this requires the access_token to be in state, hence the .then()
        dispatch(authActions.asyncActions.getUserScopeParams());
      });
    }
  }, [user]);

  const styles = React.useMemo(() => {
    const { style } = getSubdomainAssets();
    return style;
  }, []);

  return isBootstrapped ? (
    <div className="rc-h-full" style={styles as React.CSSProperties}>
      <Switch>
        <Route
          path="/reader/:cloudId/:bookId"
          render={({ match }) => (
            <Redirect
              to={`/read/${match.params.cloudId}/${match.params.bookId}`}
            />
          )}
        />
        <Route path="/native/read/:cloudId/:bookId">
          <NativeUrlHandler />
        </Route>
        <Route path="/read/:cloudId/:bookId">
          <Page title="Reader">
            <ReaderPage />
          </Page>
        </Route>
        <Route path="/content">
          <Page title="Content">
            <ContentPage
              navigateToCloudsView={() => {
                refresh();
                return history.push('/');
              }}
            />
          </Page>
        </Route>
        <Route path="/">
          <Page title="Home">
            <CloudsAndContentPage refreshing={refreshing} onRefresh={refresh} />
          </Page>
        </Route>
      </Switch>
      {analytics.isFeatureEnabled('beta-feedback-button') && user && (
        <FeedbackUI
          userEmail={user.email}
          getOpenReplaySessionURL={() => {
            const result = trackEvent('Webapp/FeedbackSent');
            if (result) {
              return `https://app.posthog.com/events/${
                result.uuid
              }/${result.timestamp.toISOString()}`;
            }
          }}
          appName="WebApp"
        />
      )}
    </div>
  ) : (
    <LoadingIcon loading position="fixed" />
  );
};

export default App;
